import { useEffect, useMemo, useState } from "react";
import {
  Form,
  Select,
  Input,
  Tag,
  Space,
  Alert,
  Tooltip,
  Spin,
  message,
} from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useAppDispatch } from "../../../../../store/hooks";
import { asyncUpdateAppData } from "../../../../../store/easyApp/easyapp-async";

const DENIAL_REASONS = [
  { code: "credit_criminal", label: "Credit & Criminal" },
  { code: "eviction", label: "Eviction" },
  { code: "income", label: "Income" },
  { code: "conditional_approval", label: "Conditional Approval" },
];

interface ApplicantObject {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  application_id: string;
}

const Create = ({ form, application, loading }: any) => {
  const [editingEmailIndex, setEditingEmailIndex] = useState<number | null>(
    null
  );
  const [editedEmail, setEditedEmail] = useState("");
  const [hoveredEmailIndex, setHoveredEmailIndex] = useState<number | null>(
    null
  );
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<{
    denialReason: string;
    selectedApplicants: ApplicantObject[];
  }>({
    denialReason: "",
    selectedApplicants: [],
  });
  const [localApplication, setLocalApplication] = useState({
    ...application,
    applicant_names: [...application.applicant_names],
  });

  const dispatch = useAppDispatch();

  const denialReasonOptions = useMemo(
    () => DENIAL_REASONS.map((r) => ({ label: r.label, value: r.code })),
    []
  );

  useEffect(() => {
    const denialReason = form.getFieldValue("denialReason") || "";
    const selectedApplicants = form.getFieldValue("selectedApplicants") || [];
    setFormValues({ denialReason, selectedApplicants });
    setEmailError(null);
  }, [form]);

  const applicantsToDisplay = useMemo(
    () => formValues.selectedApplicants || [],
    [formValues.selectedApplicants]
  );

  const validateEmail = (email: string): boolean => {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    const trimmed = email.trim().toLowerCase();
    return emailRegex.test(trimmed) && trimmed.length <= 254;
  };

  const handleFormChange = () => {
    setFormValues({
      denialReason: form.getFieldValue("denialReason") || "",
      selectedApplicants: form.getFieldValue("selectedApplicants") || [],
    });
    setEmailError(null);
  };

  const handleEmailEdit = (index: number, currentEmail: string) => {
    setEditingEmailIndex(index);
    setEditedEmail(currentEmail || "");
    setEmailError(null);
  };

  const handleEmailSave = async (index: number) => {
    if (!validateEmail(editedEmail)) {
      setEmailError("Invalid email format");
      message.error("Please enter a valid email address");
      return;
    }
    try {
      setSavingIndex(index);
      const applicant = applicantsToDisplay[index];

      await dispatch(
        asyncUpdateAppData({
          fieldName: "email",
          value: editedEmail.trim().toLowerCase(),
          applicationId: applicant.application_id,
        })
      ).unwrap();

      const updatedApplicants = formValues.selectedApplicants.map((a, i) =>
        i === index ? { ...a, email: editedEmail.trim().toLowerCase() } : a
      );

      setFormValues((prev) => ({
        ...prev,
        selectedApplicants: updatedApplicants,
      }));
      form.setFieldValue("selectedApplicants", updatedApplicants);

      const updatedApplicantNames = localApplication.applicant_names.map((a: any) =>
        a.user_id === applicant.user_id
          ? { ...a, email: editedEmail.trim().toLowerCase() }
          : a
      );
      setLocalApplication({
        ...localApplication,
        applicant_names: updatedApplicantNames,
      });

      setEditingEmailIndex(null);
      setEditedEmail("");
      setEmailError(null);
      message.success("Email updated successfully");
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Failed to update email";
      setEmailError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSavingIndex(null);
    }
  };

  const handleEmailCancel = () => {
    setEditingEmailIndex(null);
    setEditedEmail("");
    setEmailError(null);
  };

  return (
    <div style={{ padding: "24px 0" }}>
      <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
        <Form.Item
          label="Denial Reason"
          name="denialReason"
          rules={[{ required: true, message: "Please select a denial reason" }]}
          validateTrigger={["onChange", "onBlur"]}
        >
          <Select
            placeholder="Select a denial reason"
            options={denialReasonOptions}
            allowClear
          />
        </Form.Item>

        {formValues.denialReason === "conditional_approval" && (
          <Form.Item
            label="Required Additional Deposit Amount"
            name="conditionalDepositAmount"
            rules={[
              {
                required: true,
                message: "Please enter the required deposit amount",
              },
              {
                pattern: /^\d+(\.\d{1,2})?$/,
                message: "Please enter a valid amount (e.g., 500.00)",
              },
            ]}
          >
            <Input
              placeholder="Enter amount (e.g., 500.00)"
              prefix="$"
              type="number"
              step="0.01"
              min="0"
              disabled={loading}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Select Recipients"
          required
          validateStatus={
            formValues.selectedApplicants.length === 0 ? "error" : undefined
          }
          help={
            formValues.selectedApplicants.length === 0
              ? "Please select at least one applicant"
              : undefined
          }
        >
          <Select
            mode="multiple"
            placeholder="Select applicants who will receive the adverse action letter"
            value={formValues.selectedApplicants.map((a) => a.user_id)}
            onChange={(selectedUserIds) => {
              const selectedApplicantObjects = selectedUserIds.map((userId) => {
                const applicant = localApplication?.applicant_names?.find(
                  (a: any) => a.user_id === userId
                );
                return {
                  user_id: applicant.user_id,
                  first_name: applicant.first_name,
                  last_name: applicant.last_name,
                  email: applicant.email,
                  application_id: applicant.application_id,
                };
              });
              form.setFieldValue(
                "selectedApplicants",
                selectedApplicantObjects
              );
              setFormValues((prev) => ({
                ...prev,
                selectedApplicants: selectedApplicantObjects,
              }));
            }}
            disabled={loading}
          >
            {localApplication?.applicant_names?.map((applicant: any) => (
              <Select.Option key={applicant.user_id} value={applicant.user_id}>
                {applicant.first_name} {applicant.last_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {applicantsToDisplay.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h3 style={{ marginBottom: 16, fontSize: "14px", fontWeight: 600 }}>
              Verify Email Addresses
            </h3>
            <Alert
              message="Verify the email addresses below before sending adverse action letters."
              type="info"
              style={{ marginBottom: 16 }}
              showIcon
            />

            {emailError && (
              <Alert
                message="Email Update Error"
                description={emailError}
                type="error"
                style={{ marginBottom: 16 }}
                showIcon
                closable
                onClose={() => setEmailError(null)}
              />
            )}

            <div
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
                padding: "16px",
              }}
            >
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="middle"
              >
                {applicantsToDisplay.map(
                  (applicant: ApplicantObject, idx: number) => {
                    const email = applicant.email;
                    const isEditing = editingEmailIndex === idx;
                    const displayEmail = isEditing ? editedEmail : email;
                    const isValidEmail = validateEmail(displayEmail);
                    const isHovered = hoveredEmailIndex === idx;
                    const isSaving = savingIndex === idx;

                    return (
                      <div
                        key={applicant.user_id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          paddingBottom: "8px",
                          borderBottom:
                            idx < applicantsToDisplay.length - 1
                              ? "1px solid #f0f0f0"
                              : "none",
                        }}
                        onMouseEnter={() => setHoveredEmailIndex(idx)}
                        onMouseLeave={() => setHoveredEmailIndex(null)}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500 }}>
                            {applicant.first_name} {applicant.last_name}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginTop: "4px",
                            }}
                          >
                            {isEditing ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  width: "50%",
                                }}
                              >
                                <Input
                                  value={editedEmail}
                                  onChange={(e) =>
                                    setEditedEmail(e.target.value)
                                  }
                                  status={
                                    editedEmail && !isValidEmail ? "error" : ""
                                  }
                                  style={{ flex: 1 }}
                                  autoFocus
                                  disabled={isSaving}
                                />
                                {isSaving ? (
                                  <Spin size="small" />
                                ) : (
                                  <Tooltip
                                    title={
                                      isValidEmail
                                        ? "Save email"
                                        : "Invalid email format"
                                    }
                                  >
                                    <CheckOutlined
                                      style={{
                                        cursor: isValidEmail
                                          ? "pointer"
                                          : "not-allowed",
                                        color: isValidEmail
                                          ? "#52c41a"
                                          : "#d9d9d9",
                                        fontSize: "16px",
                                      }}
                                      onClick={() =>
                                        isValidEmail && handleEmailSave(idx)
                                      }
                                    />
                                  </Tooltip>
                                )}
                                <Tooltip title="Cancel">
                                  <CloseOutlined
                                    style={{
                                      cursor: "pointer",
                                      color: "#ff4d4f",
                                      fontSize: "16px",
                                    }}
                                    onClick={handleEmailCancel}
                                  />
                                </Tooltip>
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "12px",
                                    color: isValidEmail ? "#666" : "#ff4d4f",
                                  }}
                                >
                                  {email || "No email"}
                                </span>
                                {isHovered && !isSaving && (
                                  <Tooltip title="Edit email">
                                    <EditOutlined
                                      style={{
                                        cursor: "pointer",
                                        color: "#1890ff",
                                        fontSize: "14px",
                                      }}
                                      onClick={() =>
                                        handleEmailEdit(idx, email)
                                      }
                                    />
                                  </Tooltip>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <Tag color={isValidEmail ? "green" : "red"}>
                          {isValidEmail ? "Valid" : "Invalid"}
                        </Tag>
                      </div>
                    );
                  }
                )}
              </Space>
            </div>
          </div>
        )}
      </Form>
    </div>
  );
};

export default Create;